/**
 * The single chart-layout helper for the portal.
 *
 * `chartLayout(opts)` returns a finished `Partial<Layout>` — it folds the
 * per-family margin/legend/axis diffs and the repo-wide title/legend/
 * inner-tick convention into one call and runs `mergeLayout` internally, so
 * callers no longer hand-compose three layers.
 *
 *   const layout = chartLayout({ family: 'timeseries', title: 'Forecast' });
 *
 * Chart-specific overrides (axis ranges, formats, shapes, …) still layer on
 * top — `PlotlyChart` re-runs `mergeLayout`, so a plain spread is enough:
 *
 *   const layout = { ...chartLayout({ family: 'bar', title }), barmode: 'group' };
 *
 * Family diffs are derived from real callers in the codebase:
 *   - `timeseries`  — RecessionProbabilitiesTab / VolatilityTab style.
 *   - `fan`         — mars-live `buildFanLayout` (small-multiple fan grid).
 *   - `bar`         — DecompositionTab `decompositionLayout` (stacked bars).
 *   - `sparkline`   — dashboard mini sections (Returns/MP timeseries).
 */
import type { Layout } from 'plotly.js';

import { PLOTLY_FONT_FAMILY, mergeLayout } from './defaults';

export type LayoutFamily = 'timeseries' | 'fan' | 'bar' | 'sparkline';

/**
 * Timeseries — long-history line chart in a forecasts tab. Mostly the
 * portal default, with a slightly tighter top margin so titles sit
 * closer to the plot area (matches RecessionProbabilitiesTab/VolatilityTab).
 */
const TIMESERIES_FAMILY: Partial<Layout> = {
  margin: { l: 60, r: 20, t: 50, b: 50, pad: 4 },
};

/**
 * Fan — small-multiple fan chart cell. Tight margins, smaller legend
 * font, gridlines on. Mirrors `buildFanLayout` in `lib/plotly/fanChart.ts`.
 */
const FAN_FAMILY: Partial<Layout> = {
  margin: { l: 35, r: 20, t: 35, b: 30, pad: 2 },
  legend: {
    orientation: 'h',
    x: 0.5,
    xanchor: 'center',
    y: 1.0,
    yanchor: 'bottom',
    font: { size: 10 },
  },
  xaxis: {
    type: 'date',
    showgrid: true,
    gridcolor: '#DCDCDC',
    gridwidth: 1,
    hoverformat: '%b %Y',
  },
  yaxis: {
    showgrid: true,
    gridcolor: '#DCDCDC',
    gridwidth: 1,
  },
};

/**
 * Bar — stacked / relative bar chart, matches DecompositionTab's
 * `decompositionLayout`. Uses `barmode: 'relative'` so positive and
 * negative contributions stack from the zero line.
 */
const BAR_FAMILY: Partial<Layout> = {
  barmode: 'relative',
  margin: { l: 60, r: 20, t: 70, b: 50, pad: 4 },
  xaxis: {
    showgrid: false,
  },
  yaxis: {
    showgrid: true,
    gridcolor: '#E5E7EB',
  },
};

/**
 * Sparkline — dashboard mini chart cell. Tight margins, no legend.
 * Derived from ReturnsMiniSection / MPTimeseriesMiniSection.
 */
const SPARKLINE_FAMILY: Partial<Layout> = {
  margin: { l: 40, r: 10, t: 10, b: 30, pad: 2 },
  showlegend: false,
};

const FAMILY_DIFFS: Record<LayoutFamily, Partial<Layout>> = {
  timeseries: TIMESERIES_FAMILY,
  fan: FAN_FAMILY,
  bar: BAR_FAMILY,
  sparkline: SPARKLINE_FAMILY,
};

/** Options accepted by {@link chartLayout}. All fields are optional. */
export interface ChartLayoutOptions {
  /**
   * Chart family — folds in per-family margin/legend/axis diffs. Omit for
   * the rare layout that wants only the global convention (e.g. the
   * Returns-by-Horizon bar panel in `AssetSupportingPanels`).
   */
  family?: LayoutFamily;
  /** Title rendered at top of plot (omit if title is in DOM ChartCard h3). */
  title?: string;
  /** Margin overrides; merged onto the convention's defaults. */
  marginOverride?: Partial<Layout['margin']>;
  /** Default true. Set false for sparkline/embedded charts. */
  showLegend?: boolean;
  /** Default true. Set false for charts that opt out of inner ticks (rare). */
  innerTicks?: boolean;
}

/**
 * Repo-wide chart layout.
 *
 * For the `timeseries` / `bar` / `sparkline` families (and the family-less
 * default) this is: title centered at top -> horizontal legend BETWEEN
 * title and chart drawing area -> inner x/y ticks -> axis labels with
 * breathing room.
 *
 * The `fan` family is deliberately different — the small-multiple fan grid
 * has its own layout language (tight margins, 10px legend pinned at y:1.0,
 * 14px title) and does NOT take the repo-wide convention. `chartLayout`
 * surfaces those fan values so `buildFanLayout` can source title/legend/
 * margin from one place instead of hardcoding them.
 *
 * The returned object is already merged over `DEFAULT_LAYOUT` via
 * `mergeLayout`, so it is a finished `Partial<Layout>` ready to hand to
 * `<PlotlyChart>`.
 *
 * Pages may render the title either in DOM (via `<ChartCard title="...">`)
 * or inside Plotly (via `chartLayout({ title })`) -- never both.
 *
 * Example:
 *   const layout = chartLayout({ family: 'timeseries', title: 'Forecast' });
 */
export function chartLayout(opts: ChartLayoutOptions = {}): Partial<Layout> {
  const { family, title, marginOverride, showLegend = true, innerTicks = true } = opts;

  // The fan grid has its own layout language — tight margins, a 10px legend
  // at y:1.0, a 12px title. It does NOT take the title/legend/inner-tick
  // convention below. `buildFanLayout` pulls title/legend/margin off this.
  //
  // Title size is 12 (not the repo-wide 18) to match the production small-
  // multiple fan grid — the live portal renders these cells with a compact
  // 12px title and 10px legend so the 6-up grid stays readable. A larger
  // title forces the legend to wrap and squashes the plot area.
  if (family === 'fan') {
    return mergeLayout({
      ...FAN_FAMILY,
      ...(title
        ? {
            title: {
              text: title,
              x: 0.5,
              xanchor: 'center',
              y: 0.98,
              yanchor: 'top',
              font: { size: 12, color: '#000000' },
            },
          }
        : {}),
      showlegend: showLegend,
      margin: { ...FAN_FAMILY.margin, ...(marginOverride ?? {}) },
    });
  }

  const tickProps = innerTicks
    ? {
        ticks: 'inside' as const,
        ticklen: 6,
        tickwidth: 1,
        tickcolor: '#000000',
      }
    : {};

  const familyDiff = family ? FAMILY_DIFFS[family] : {};

  // Replicate the historic `mergeLayout({ ...layoutPreset(family),
  // ...chartLayoutPreset(opts) })` composition exactly: the convention's
  // keys (margin/xaxis/yaxis/showlegend/legend/title) shallow-replace the
  // family's same keys, then `mergeLayout` deep-merges over DEFAULT_LAYOUT.
  return mergeLayout({
    ...familyDiff,
    ...(title
      ? {
          title: {
            text: title,
            // 18 matches legacy `setTitleAndLegend` (`titleDefaults.fontsize`).
            font: { size: 18, family: PLOTLY_FONT_FAMILY, color: '#000000' },
            x: 0.5,
            xanchor: 'center',
            y: 0.98,
            yanchor: 'top',
          },
        }
      : {}),
    showlegend: showLegend,
    ...(showLegend
      ? {
          legend: {
            orientation: 'h',
            x: 0.5,
            xanchor: 'center',
            y: 1.02, // legend sits just above the chart drawing area
            yanchor: 'bottom',
            // 16 matches legacy `setTitleAndLegend` (legend default resolves
            // to 16 via the unset `layout.font.size`).
            font: { size: 16, family: PLOTLY_FONT_FAMILY, color: '#000000' },
          },
        }
      : {}),
    margin: {
      // No `pad` — the extra inset visibly shifts the drawing area.
      l: 50,
      r: 20,
      t: 85,
      b: 40,
      ...(marginOverride ?? {}),
    },
    xaxis: tickProps,
    yaxis: tickProps,
  });
}

/**
 * Standoff value applied to axis titles by the (now-removed-from-helper)
 * convention: 10px breathing room between tick labels and the axis title.
 * Charts that want an axis title should spread `axisTitleStandoff` into
 * their `title` block, e.g.:
 *
 *   yaxis: { ...layout.yaxis, title: { text: '% Ann.', ...axisTitleStandoff } }
 */
export const axisTitleStandoff = { standoff: 10 } as const;
